import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';
import 'swiper/swiper-bundle.min.css';

import 'swiper/swiper.min.css';

import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';

import { FETCH_STATUSES, Nullish, useMedia } from '@tager/web-core';

import ProductCard from '@/components/ProductCard';
import { useTypedSelector } from '@/store/store';
import { selectProductRelationListResource } from '@/store/reducers/pages/product/product';
import Link from '@/components/Link';
import { colors } from '@/constants/theme';
SwiperCore.use([Navigation]);

type Props = {
  id: Nullish<number>;
};

function Relations({ id }: Props) {
  const data = useTypedSelector((state) => {
    if (id) {
      return selectProductRelationListResource(state, id);
    }
  });

  const shouldCheckItemsAmount = useMedia(`(min-width: 500px)`);

  return (
    <Component>
      {data?.status === FETCH_STATUSES.SUCCESS
        ? data.data.map((content, index) => {
            return !!content.items.length ? (
              <Content key={index} className="product-item-content">
                <Header>
                  <Label>{content.name}</Label>
                  {(!shouldCheckItemsAmount && content.items.length > 1) ||
                  (shouldCheckItemsAmount && content.items.length > 2) ? (
                    <Nav>
                      <Prev className={'prev-product-relation-item' + index} />
                      <Next className={'next-product-relation-item' + index} />
                    </Nav>
                  ) : null}
                </Header>
                {(!shouldCheckItemsAmount && content.items.length > 1) ||
                (shouldCheckItemsAmount && content.items.length > 2) ? (
                  <Swiper
                    navigation={{
                      prevEl: '.prev-product-relation-item' + index,
                      nextEl: '.next-product-relation-item' + index,
                    }}
                    slidesOffsetBefore={15}
                    slidesOffsetAfter={15}
                    spaceBetween={15}
                    slidesPerView={1.25}
                    slidesPerGroup={1}
                    breakpoints={{
                      500: {
                        slidesPerView: 2.2,
                      },
                    }}
                  >
                    {content.items.length === 1 ? (
                      <SingleCardWrapper>
                        <Link to={'/products/' + content.items[0].urlAlias}>
                          <ProductCard {...content.items[0]} />
                        </Link>
                      </SingleCardWrapper>
                    ) : (
                      content.items.map((item) => {
                        return (
                          <SwiperSlide key={item.id}>
                            <Link to={'/products/' + item.urlAlias}>
                              <ProductCard {...item} />
                            </Link>
                          </SwiperSlide>
                        );
                      })
                    )}
                  </Swiper>
                ) : (
                  <ProductWrapper>
                    {content.items.map((item, index) => (
                      <Link to={'/products/' + item.urlAlias}>
                        <ProductCard {...item} />
                      </Link>
                    ))}
                  </ProductWrapper>
                )}
              </Content>
            ) : null;
          })
        : null}
    </Component>
  );
}

export default Relations;

const Component = styled.div`
  & > .product-item-content:not(:first-child) {
    margin-top: 20px;
  }
`;
const Content = styled.div``;
const Label = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 22px;
  line-height: 40px;
`;

const Header = styled.div`
  padding: 0 15px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Nav = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 0 0 46px;
`;

const Prev = styled.div`
  cursor: pointer;
  position: relative;
  height: 22.5px;
  width: 15px;
  &:before {
    position: absolute;
    border-radius: 5px 0 0 5px;
    content: '';
    top: 0;
    right: 0;
    height: 1px;
    width: 16px;
    transform-origin: 100% 0;
    transform: rotate(315deg);
    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:after {
    position: absolute;
    border-radius: 5px 0 0 5px;
    content: '';
    bottom: 0;
    right: 0;
    height: 1px;
    width: 15px;
    transform-origin: 100% 0;
    transform: rotate(45deg);
    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:hover:before,
  &:hover:after {
    background-color: ${colors.black300};
  }
`;
const Next = styled.div`
  cursor: pointer;
  position: relative;
  height: 22.5px;
  width: 15px;

  &:before {
    cursor: pointer;
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    height: 1px;
    width: 16px;
    transform-origin: 0 0;
    transform: rotate(45deg);
    border-radius: 0 5px 5px 0;
    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:after {
    cursor: pointer;
    position: absolute;
    content: '';
    bottom: 0;
    left: 0;
    height: 1px;
    width: 15px;
    transform-origin: 0 0;
    transform: rotate(-45deg);
    border-radius: 0 5px 5px 0;

    background-color: ${colors.black};
    transition: background-color 0.25s ease-in-out;
  }

  &:hover:before,
  &:hover:after {
    background-color: ${colors.black300};
  }
`;

const SingleCardWrapper = styled.div`
  padding: 0 15px;
  max-width: 480px;
`;

const ProductWrapper = styled.div`
  padding: 0 15px;
  display: grid;
  grid-template-columns: calc(50% - 7.5px) calc(50% - 7.5px);
  gap: 15px;
`;
